import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OrderArrow from "../../VectorIcons/OrderArrow";
import TextInput from "../Input/TextInput";
import Pagination from "../Pagination/Pagination";
import Tag from "../Tag/Tag";
import IconButton from "../Button/IconButton";
import { ActionsMenu } from "../../ActionsMenu/ActionsMenu";
import { FONT_INTER } from "../../../styles/fonts";

const headerCellSx = {
  fontFamily: FONT_INTER,
  color: "#646568",
  fontSize: 13,
  fontWeight: 700,
  borderBottom: "none",
  whiteSpace: "nowrap",
  py: "8px",
  backgroundColor: "#F7F8FA",
};

const bodyCellSx = {
  fontFamily: FONT_INTER,
  fontSize: 14,
  color: "#252A34",
  py: "8px",
  borderBottom: "1px solid #E2E2E3",
};

const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const getSearchableValue = (row, column) => {
  const value = row[column.key];
  return Array.isArray(value) ? value.join(" ") : value;
};

const SearchableTable = ({
  columns,
  data,
  searchPlaceholder = "Buscar",
  searchKeys,
  rowsPerPage = 10,
  rowsPerPageOptions = [10, 25, 50],
  itemsLabel = "itens",
  loading = false,
  emptyStateMessage = "Nenhum resultado encontrado.",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(rowsPerPage);
  const [sortConfig, setSortConfig] = useState(null);

  const searchableColumns = useMemo(
    () =>
      columns.filter((column) =>
        searchKeys ? searchKeys.includes(column.key) : column.type !== "actions"
      ),
    [columns, searchKeys]
  );

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const term = normalize(searchTerm);

    return data.filter((row) =>
      searchableColumns.some((column) =>
        normalize(getSearchableValue(row, column)).includes(term)
      )
    );
  }, [data, searchTerm, searchableColumns]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const { key, direction } = sortConfig;

    return [...filteredData].sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];
      const comparison = String(valueA ?? "").localeCompare(
        String(valueB ?? "")
      );
      return direction === "asc" ? comparison : -comparison;
    });
  }, [filteredData, sortConfig]);

  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleSearchClick = () => {
    setPage(1);
  };

  const handleSort = (columnKey) => {
    setSortConfig((current) => {
      if (current?.key !== columnKey)
        return { key: columnKey, direction: "asc" };
      if (current.direction === "asc")
        return { key: columnKey, direction: "desc" };
      return null;
    });
  };

  const handleRowsPerPageChange = (value) => {
    setPageSize(value);
    setPage(1);
  };

  const renderCell = (column, row) => {
    if (column.render) return column.render(row);

    if (column.type === "tags") {
      const items = row[column.key] || [];
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            maxWidth: column.width,
            minWidth: 0,
          }}
        >
          {items.map((item) => (
            <Tag
              key={item}
              label={item}
              sx={{
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            />
          ))}
        </Box>
      );
    }

    if (column.type === "actions") {
      return <ActionsMenu menuOptions={row.actions} />;
    }

    return row[column.key];
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        overflow: "hidden",
        fontFamily: FONT_INTER,
      }}
    >
      <Box sx={{ p: 3, pt: "48px" }}>
        <TextInput
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
          autoComplete="off"
          sx={{ width: "100%", maxWidth: 575 }}
          endAdornment={
            <IconButton
              icon={<SearchIcon sx={{ color: "#9a9aa8" }} fontSize="small" />}
              onClick={handleSearchClick}
              aria-label="Buscar"
              sx={{ p: 0.5 }}
            />
          }
        />
      </Box>

      <Box sx={{ p: 3, mb: "96px" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "left"}
                    sx={{
                      ...headerCellSx,
                      width: column.width,
                      ...(index === 0 && {
                        borderTopLeftRadius: 8,
                        borderBottomLeftRadius: 8,
                      }),
                      ...(index === columns.length - 1 && {
                        borderTopRightRadius: 8,
                        borderBottomRightRadius: 8,
                      }),
                    }}
                  >
                    {column.sortable ? (
                      <Box
                        component="span"
                        onClick={() => handleSort(column.key)}
                        sx={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          userSelect: "none",
                        }}
                      >
                        {column.label}
                        <OrderArrow
                          upColor={
                            sortConfig?.key === column.key &&
                            sortConfig.direction === "asc"
                              ? "#666666"
                              : "#CCCCCC"
                          }
                          downColor={
                            sortConfig?.key === column.key &&
                            sortConfig.direction === "desc"
                              ? "#666666"
                              : "#CCCCCC"
                          }
                          style={{ width: 20, height: 20 }}
                        />
                      </Box>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && paginatedData.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={bodyCellSx}
                  >
                    {emptyStateMessage}
                  </TableCell>
                </TableRow>
              )}

              {paginatedData.map((row, index) => (
                <TableRow key={row.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={{
                        ...bodyCellSx,
                        width: column.width,
                        // extra top padding on the first row so it isn't flush against the header
                        ...(index === 0 && { pt: "16px" }),
                      }}
                    >
                      {renderCell(column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsLabel={itemsLabel}
        rowsPerPage={pageSize}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default SearchableTable;
